import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { Logo } from "./Logo";
import { FlagRow } from "./Flags";
import { NAV_LINKS, WINGS } from "@/lib/site";

const MenuOverlay = ({ onClose }) => (
  <motion.div
    initial={{ clipPath: "inset(0 0 100% 0)" }}
    animate={{ clipPath: "inset(0 0 0% 0)" }}
    exit={{ clipPath: "inset(0 0 100% 0)" }}
    transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
    className="fixed inset-0 z-40 flex flex-col justify-between bg-[#0E0E10] px-6 pb-10 pt-28 text-[#F1ECE2] sm:px-10"
    data-testid="mobile-menu"
  >
    <nav className="flex flex-col gap-1">
      {[{ to: "/", label: "home", testid: "menu-home-link" }, ...NAV_LINKS].map((l, i) => (
        <motion.div key={l.to} initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.25 + i * 0.06, duration: 0.6 }}>
          <Link to={l.to} onClick={onClose} data-testid={`m-${l.testid}`} className="font-display flex items-baseline gap-4 text-5xl font-bold tracking-tight hover:text-[#FF4D1A] sm:text-7xl">
            <span className="font-mono-x text-xs text-[#a8a29a]">0{i + 1}</span>{l.label}
          </Link>
        </motion.div>
      ))}
    </nav>
    <div className="font-mono-x flex flex-col gap-1 text-sm text-[#cfc9be]">
      <a href={`mailto:${WINGS.pitsus.email}`}>{WINGS.pitsus.email}</a>
      <a href={`mailto:${WINGS.pitsind.email}`}>{WINGS.pitsind.email}</a>
    </div>
  </motion.div>
);

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [solid, setSolid] = useState(false);
  const { scrollY } = useScroll();
  const { pathname } = useLocation();

  useMotionValueEvent(scrollY, "change", (v) => {
    const prev = scrollY.getPrevious() ?? 0;
    setHidden(v > prev && v > 240 && !open);
    setSolid(v > 40);
  });

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (open) window.__lenis?.stop(); else window.__lenis?.start();
  }, [open]);

  return (
    <>
      <motion.header
        animate={{ y: hidden ? "-110%" : "0%" }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${solid && !open ? "bg-[#F1ECE2]/80 backdrop-blur-xl border-b border-black/10" : ""}`}
        data-testid="site-header"
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 sm:px-8">
          <Logo light={open} />
          <nav className="hidden items-center gap-7 lg:flex">
            {NAV_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={l.testid} className={({ isActive }) => `ul-draw text-[0.95rem] font-medium ${isActive ? "active" : ""}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <FlagRow className="hidden sm:inline-flex" size="w-[18px]" />
            <Link to="/contact" data-testid="nav-brief-btn" className="hidden rounded-full bg-[#0E0E10] px-5 py-2.5 text-sm font-semibold text-[#F1ECE2] transition-colors duration-300 hover:bg-[#FF4D1A] md:inline-flex">
              brief us
            </Link>
            <button onClick={() => setOpen((o) => !o)} data-testid="menu-toggle-btn" aria-label="Toggle menu"
              className={`relative flex h-11 w-11 items-center justify-center rounded-full lg:hidden ${open ? "bg-[#F1ECE2]" : "bg-[#0E0E10]"}`}>
              <span className={`absolute h-[2px] w-5 transition-transform duration-300 ${open ? "rotate-45 bg-[#0E0E10]" : "-translate-y-1 bg-[#F1ECE2]"}`} />
              <span className={`absolute h-[2px] w-5 transition-transform duration-300 ${open ? "-rotate-45 bg-[#0E0E10]" : "translate-y-1 bg-[#F1ECE2]"}`} />
            </button>
          </div>
        </div>
      </motion.header>
      <AnimatePresence>{open && <MenuOverlay onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
}
